import { Injectable } from "@angular/core";
import { Region, UserRole } from "./api/api.service";
import { Observable } from "rxjs";
import { HttpClient, HttpContext, HttpParams, HttpResponse } from "@angular/common/http";
import { getApiUrl } from "@shared/utils/app-util";
import { SHOW_LOADER } from "@shared/components/loader/interceptors/loader.interceptor";

export interface AuditLogs {
  id: number;
  action: string;
  category: string;
  description: string;
  timestamp: string;
  target_id: number;
  target_name: string;
  target_type: string;
  target_status: null | string;
  country_ids: number[];
  user_info: UserInfo;
}

export interface UserInfo {
  user_id: number;
  user_name: string;
  email: string;
  role_id: number;
  region_id: number;
  responsible_countries: number[];
}

export interface Permission {
  permission_id: number;
  has_permission: boolean;
}
export interface userPermission {
  role_id: number;
  permissions: Permission[];
}
export interface Users extends UserRole {
  email?: string; // optional field
}
export interface PermissionReference extends Region {
  description: string;
}
export interface Permission {
  permission_id: number;
  has_permission: boolean;
}
export interface userPermission {
  role_id: number;
  permissions: Permission[];
}
export interface Role {
  label: string;
  value: string;
}

// Country interface
export interface Country {
  id: number;
  name: string;
  region: Region;
  added_at: string; // ISO date string format
}
@Injectable({
  providedIn: "root",
})
export class AuditPermissionService {
  constructor(private http: HttpClient) {}

  getAuditLogs(
    level: string,
    page: number,
    pageSize: number,
    filters?: any
  ): Observable<AuditLogs> {
    let params = new HttpParams()
      .set("page", page.toString())
      .set("page_size", pageSize.toString());
    if (filters) {
      params = this.setParamsFilter(params, filters);
    }
    const baseUrl = getApiUrl("GET_AUDIT_LOG");
    const url = `${baseUrl}/${level}`;
    return this.http.get<AuditLogs>(url, { params });
  }

  setParamsFilter(params: any, filters: any) {
    if (filters) {
      for (const key in filters) {
        if (
          filters.hasOwnProperty(key) &&
          filters[key] !== null &&
          filters[key] !== undefined &&
          filters[key] !== ""
        ) {
          const value = filters[key];

          // This part correctly handles date ranges (from and to)
          if (
            Array.isArray(value) &&
            value.length > 0 &&
            value[0] instanceof Date
          ) {
            const fromDate = value[0].toISOString().split("T")[0];
            params = params.set(`${key}_from`, fromDate);
            if (value[1]) {
              const toDate = value[1].toISOString().split("T")[0];
              params = params.set(`${key}_to`, toDate);
            }
          }
          // This part handles the {from: Date, to: Date} object from the table component
          else if (
            typeof value === "object" &&
            !Array.isArray(value) &&
            value.from &&
            value.to
          ) {
            const fromDate = new Date(value.from).toISOString().split("T")[0];
            params = params.set(`${key}_from`, fromDate);
            const toDate = new Date(value.to).toISOString().split("T")[0];
            params = params.set(`${key}_to`, toDate);
          }
          // This part handles multiselect arrays (e.g., ['value1', 'value2'])
          else if (Array.isArray(value)) {
            params = params.set(key, value.join(","));
          }
          // Fallback for primitive types (string, number, boolean)
          else {
            params = params.set(key, value);
          }
        }
      }
    }
    return params;
  }

  getDropdownAuditFIlterLogs(level: string) {
    const baseUrl = getApiUrl("GET_DROPDOWN_AUDIT_FILTER");
    const url = `${baseUrl}/${level}`;
    return this.http.get<any>(url);
  }

  exportAuditLogs(
    level: string,
    page: number,
    pageSize: number,
    filters?: any
  ): Observable<HttpResponse<Blob>> {
    let params = new HttpParams()
      .set("page", page.toString())
      .set("page_size", pageSize.toString())
      .set("export_excel", "true");
    
    if (filters) {
      params = this.setParamsFilter(params, filters);
    }
    
    const baseUrl = getApiUrl("GET_AUDIT_LOG");
    const url = `${baseUrl}/${level}`;
    
    return this.http.get(url, {
      params,
      responseType: "blob",
      observe: "response",
    });
  }

  // permission roles

  getPermissionRole(): Observable<any> {
    const url = getApiUrl("GET_ROLE");
    return this.http.get<any>(url);
  }
  getPermissionReference(): Observable<any> {
    const url = getApiUrl("GET_PERMISSION_REFERENCE");
    return this.http.get<any>(url);
  }
  getGlobalUserName(
    page?: number,
    pageSize?: number,
    search?: string
  ): Observable<any> {
    let params = new HttpParams()
      .set("page", (page ?? 1).toString())
      .set("page_size", (pageSize ?? 10).toString());

    if (search) {
      params = params.set("search", search);
    }

    const url = getApiUrl("GET_GLOBAL_USER_NAME");
    return this.http.get<any>(url, { params, context: new HttpContext().set(SHOW_LOADER, false) });
  }

  /**
   * Fetches Global RA users excluding those already associated with a specific RCA
   * @param excludeRcaId RCA ID to exclude users from
   * @param page Page number for pagination
   * @param pageSize Number of items per page
   * @param search Search term
   * @returns Observable of filtered users
   */
  getGlobalUserNameExcludingRca(
    excludeRcaId: number,
    page?: number,
    pageSize?: number,
    search?: string
  ): Observable<any> {
    let params = new HttpParams()
      .set("page", (page ?? 1).toString())
      .set("page_size", (pageSize ?? 10).toString())
      .set("exclude_rca_id", excludeRcaId.toString());

    if (search) {
      params = params.set("search", search);
    }

    const url = getApiUrl("GET_GLOBAL_USER_NAME");
    return this.http.get<any>(url, { params, context: new HttpContext().set(SHOW_LOADER, false) });
  }
  getPermissionUserByID(id: any): Observable<any> {
    const baseUrl = getApiUrl("GET_PERMISSION_USER");
    const url = `${baseUrl}/${id}`;
    return this.http.get<any>(url);
  }

  addCountry(payload: any): Observable<any> {
    const url = getApiUrl("GET_COUNTRY");
    return this.http.post(url, payload);
  }
  updateCountry(countryId: any, payload: any): Observable<any> {
    const baseUrl = getApiUrl("GET_COUNTRY");
    const url = `${baseUrl}/${countryId}`;
    return this.http.put(url, payload);
  }

  getCountries(
    page: number,
    pageSize: number,
    filters?: any
  ): Observable<AuditLogs> {
    let params = new HttpParams()
      .set("page", page.toString())
      .set("page_size", pageSize.toString());
    const url = getApiUrl("GET_COUNTRY");
    return this.http.get<AuditLogs>(url, { params });
  }

  postUserPermission(userId: any, payload: any) {
    const baseUrl = getApiUrl("GET_PERMISSION_USER");
    const url = `${baseUrl}/${userId}`;
    return this.http.post<any>(url, payload);
  }

  addTypeOfChanges(payload: any): Observable<any> {
    const url = getApiUrl("GET_TYPE_OF_CHANGES");
    return this.http.post(url, payload);
  }
  updateTypeOfChanges(id: any, payload: any): Observable<any> {
    const baseUrl = getApiUrl("GET_TYPE_OF_CHANGES");
    const url = `${baseUrl}/${id}`;
    return this.http.put(url, payload);
  }

  getTypeOfChanges(
    page: number,
    pageSize: number,
    filters?: any
  ): Observable<any> {
    let params = new HttpParams()
      .set("page", page.toString())
      .set("page_size", pageSize.toString());
    const url = getApiUrl("GET_TYPE_OF_CHANGES");
    return this.http.get<any>(url, { params });
  }

  getChangeTypeCategories(): Observable<any> {
    const url = getApiUrl("GET_TYPE_CHANGES_CATEGORIES");
    return this.http.get<any>(url);
  }

  addProductFamily(payload: any): Observable<any> {
    const url = getApiUrl("GET_PRODUCT_FAMILIES_CONF");
    return this.http.post(url, payload);
  }
  updateProductFamily(id: any, payload: any): Observable<any> {
    const baseUrl = getApiUrl("GET_PRODUCT_FAMILIES_CONF");
    const url = `${baseUrl}/${id}`;
    return this.http.put(url, payload);
  }

  getProductFamily(
    page: number,
    pageSize: number,
    filters?: any
  ): Observable<any> {
    let params = new HttpParams()
      .set("page", page.toString())
      .set("page_size", pageSize.toString());
    const url = getApiUrl("GET_PRODUCT_FAMILIES_CONF");
    return this.http.get<any>(url, { params });
  }
  updatePermission(
    roleId: string | number,
    permissionId: string | number,
    has_permission: boolean
  ): Observable<any> {
    let params = new HttpParams().set("has_permission", has_permission);
    const baseUrl = getApiUrl("GET_ROLE");
    const url = `${baseUrl}/${roleId}/permissions/${permissionId}`;
    return this.http.put(url, {},{ params });
  }

  // captcha authenticate
  authCaptcha(payload: any): Observable<any> {
    const url = getApiUrl("AUTH_CAPTCHA");
    return this.http.post(url, payload);
  }

  getLeave(include_cancelled: boolean = false): Observable<AuditLogs> {
    let params = new HttpParams().set(
      "include_cancelled",
      include_cancelled.toString()
    );
    const url = getApiUrl("LEAVE_USER");
    return this.http.get<AuditLogs>(url, { params });
  }

  postLeaveRequest(payload: any): Observable<any> {
    const url = getApiUrl("USER_LEAVE_REQUEST");
    return this.http.post(url, payload);
  }
  updateLeaveRequest(id: any, payload: any): Observable<any> {
    const baseUrl = getApiUrl("USER_LEAVE_UPDATE");
    const url = `${baseUrl}/${id}`;
    return this.http.put(url, payload);
  }
  deleteLeaveRequest(id: any): Observable<any> {
    const baseUrl = getApiUrl("USER_LEAVE_UPDATE");
    const url = `${baseUrl}/${id}`;
    return this.http.delete(url);
  }
}
